import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
    type ReactNode,
} from 'react';
import { locationsApi } from '@/api/locations';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';
import type { Location } from '@/types/types';

export interface LocationsContextValue {
    state: {
        locations: Location[];
        activeLocation: Location | null;
        loading: boolean;
        loaded: boolean;
    };
    actions: {
        refreshLocations: () => Promise<void>;
        createLocation: (payload: Partial<Location>) => Promise<Location | null>;
        updateLocation: (id: string, payload: Partial<Location>) => Promise<void>;
        setActiveLocation: (loc: Location | null) => void;
        setActiveLocationById: (id: string) => void;
    };
}

const LocationsContext = createContext<LocationsContextValue | null>(null);

export const LocationsProvider = ({ children }: { children: ReactNode }) => {
    const [locations, setLocations] = useState<Location[]>([]);
    const [activeLocation, setActiveLocation] = useState<Location | null>(null);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const auth = useAuth();
    const loggedIn = auth.state.loggedIn;

    useEffect(() => {
        if (!loggedIn) {
            setLocations([]);
            setActiveLocation(null);
            setLoading(false);
            setLoaded(false);
        }
    }, [loggedIn]);

    const hydrateLocation = useCallback(async (id: string) => {
        try {
            const res = await locationsApi.byId(id);
            const full = res.data?.payload;
            if (!full) return;
            setLocations((prev) => prev.map((l) => (l.id === id ? full : l)));
            setActiveLocation((prev) => (prev?.id === id ? full : prev));
        } catch {}
    }, []);

    const refreshLocations = useCallback(async () => {
        setLoading(true);
        try {
            const res = await locationsApi.list();
            const list = res.data?.payload ?? [];
            setLocations(list);
            const first = list[0] ?? null;
            setActiveLocation((prev) => prev ?? first);
            if (first) void hydrateLocation(first.id);
        } catch {
            setLocations([]);
        } finally {
            setLoading(false);
            setLoaded(true);
        }
    }, [hydrateLocation]);

    const createLocation = useCallback(
        async (payload: Partial<Location>): Promise<Location | null> => {
            try {
                const res = await locationsApi.create(payload);
                const created = res.data?.payload;
                if (created) {
                    setLocations((prev) => [...prev, created]);
                    setActiveLocation(created);
                    return created;
                }
                return null;
            } catch {
                return null;
            }
        },
        [],
    );

    const updateLocation = useCallback(
        async (id: string, payload: Partial<Location>) => {
            try {
                const res = await locationsApi.update(id, payload);
                const updated = res.data?.payload;
                if (updated) {
                    setLocations((prev) =>
                        prev.map((l) => (l.id === id ? updated : l)),
                    );
                    setActiveLocation((prev) =>
                        prev?.id === id ? updated : prev,
                    );
                }
            } catch {}
        },
        [],
    );

    const setActiveLocationById = useCallback((id: string) => {
        setActiveLocation((prev) => {
            if (prev?.id === id) return prev;
            return locations.find((l) => l.id === id) ?? prev;
        });
        void hydrateLocation(id);
    }, [locations, hydrateLocation]);

    return (
        <LocationsContext.Provider
            value={{
                state: { locations, activeLocation, loading, loaded },
                actions: {
                    refreshLocations,
                    createLocation,
                    updateLocation,
                    setActiveLocation,
                    setActiveLocationById,
                },
            }}
        >
            {children}
        </LocationsContext.Provider>
    );
};

export const useLocations = (): LocationsContextValue => {
    const ctx = useContext(LocationsContext);
    if (!ctx) throw new Error('useLocations must be used inside LocationsProvider');
    return ctx;
};
